import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllTransactions, clearFlags } from '../../store/slices/transactionSlice';
import { getDocumentsByTransactionId, clearDocuments } from '../../store/slices/documentSlice';
import CreateTransactionModal from '../../components/ui/modals/CreateTransactionModal';
import {
  Card, Typography, Tag, Row, Col, Tooltip,
  PlusOutlined, EyeOutlined,
  CustomLoader, CustomButton, CustomSelect, CustomDatePicker, CustomTable,
  Colors, Constants, showToast, getDateFromDateString, removeUnderScoreAndCapitalize
} from '../../utils/commonImports';

const { Title } = Typography;

const Transactions = () => {
  const dispatch = useDispatch();
  const { transactions, pagination, loading, isUpdated, isCreated, isBulkCreated, error } = useSelector((state) => state.transaction);
  const { documents, loading: documentLoading } = useSelector((state) => state.document);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(Constants.ITEMS_PER_PAGE);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [parentTransactionId, setParentTransactionId] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  
  // Filter states
  const initialFilters = {
    accountType: '',
    businessStatus: '',
    approvalStatus: '',
    startDate: null,
    endDate: null
  }
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    const filterParams = {
      page: currentPage,
      perPage: limit,
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
    };
    
    if (filters.startDate) filterParams.startDate = filters.startDate.format('YYYY-MM-DD');
    if (filters.endDate) filterParams.endDate = filters.endDate.format('YYYY-MM-DD');
    
    dispatch(getAllTransactions(filterParams));
  }, [dispatch, currentPage, limit, filters]);

  useEffect(() => {
    if (isUpdated || isCreated || isBulkCreated) {
      dispatch(clearFlags());
      dispatch(getAllTransactions({ page: currentPage, perPage: limit }));
    }

    if (error) {
      showToast(error, 'error');
    }
  }, [isUpdated, isCreated, isBulkCreated, dispatch, currentPage, limit, error]);

  useEffect(() => {
    if (documents && documents.length > 0 && !documentLoading) {
      const document = documents[0];
      if (document && document.fileUrl) {
        window.open(document.fileUrl, '_blank');
      }
    }
  }, [documents, documentLoading]);

  useEffect(() => {
    return () => {
      dispatch(clearDocuments());
    };
  }, [dispatch]);

  const getAccountTypeColor = (type) => {
    return type === 'corporate' ? Colors.primary : Colors.info;
  };

  const getStatusColor = (status) => {
    return status === 'inhouse' ? Colors.primary : Colors.warning;
  };

  const getApprovalStatusColor = (status) => {
    switch (status) {
      case Constants.APPROVAL_STATUS.PENDING:
        return Colors.warning;
      case Constants.APPROVAL_STATUS.APPROVED:
        return Colors.success;
      case Constants.APPROVAL_STATUS.REJECTED:
        return Colors.error;
      default:
        return Colors.gray;
    }
  };

  const renderDescriptionWithToggle = (text = '', id) => {
    const expanded = expandedDescriptions[id];
    const shouldClamp = text.length > 0;
    const clampStyle = {
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden'
    };

    return (
      <div>
        <div style={expanded ? {} : clampStyle}>
          {text || '-'}
        </div>
        {shouldClamp && text.length > 120 && (
          <span
            onClick={() => setExpandedDescriptions(prev => ({ ...prev, [id]: !expanded }))}
            style={{ color: Colors.primary, cursor: 'pointer', fontSize: '11px' }}
          >
            {expanded ? 'See less' : 'See more'}
          </span>
        )}
      </div>
    );
  };

  const handleCreate = () => {
    setParentTransactionId(null);
    setCreateModalOpen(true);
  };

  const handleCreateInstallment = (transactionId) => {
    setParentTransactionId(transactionId);
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setParentTransactionId(null);
  };

  const handleViewDocuments = (transaction) => {
    dispatch(getDocumentsByTransactionId(transaction._id));
  };

  const renderInstallmentCard = (installment, index) => (
    <div 
      key={installment._id || index}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: Colors.installmentBackground,
        border: `1px solid ${Colors.installmentBorder}`,
        borderRadius: '6px',
        boxShadow: Colors.installmentShadow,
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
        <div style={{ 
          minWidth: '70px',
          fontSize: '11px',
          color: Colors.textSecondary,
          textAlign: 'center'
        }}>
          {getDateFromDateString(installment.date)}
        </div>
        
        <div style={{ 
          flex: 1,
          fontSize: '12px',
          color: '#333',
          fontWeight: '500'
        }}>
          {installment.description}
        </div>
        
        <div style={{
          fontSize: '13px',
          fontWeight: '600',
          color: Colors.primary,
          minWidth: '70px',
          textAlign: 'right'
        }}>
          ₹{installment.total.toFixed(2)}
        </div>
        
        <div style={{
          fontSize: '10px',
          color: Colors.textTertiary,
          minWidth: '100px',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          {installment.userId ? `${installment.userId.firstName} ${installment.userId.lastName}` : 'Unknown'}
        </div>

        <div style={{
          minWidth: '100px',
          textAlign: 'center'
        }}>
          {installment.approvalStatus ? (
            <Tag color={getApprovalStatusColor(installment.approvalStatus)} style={{ 
              textTransform: 'capitalize',
              fontSize: '10px',
              padding: '2px 8px',
              margin: 0
            }}>
              {installment.approvalStatus}
            </Tag>
          ) : (
            <Tag color={Colors.gray} style={{ 
              textTransform: 'capitalize',
              fontSize: '10px',
              padding: '2px 8px',
              margin: 0
            }}>
              N/A
            </Tag>
          )}
        </div>
      </div>
      
      <div style={{ marginLeft: '12px' }}>
        <EyeOutlined 
          onClick={() => handleViewDocuments(installment)}
          style={{ 
            cursor: 'pointer', 
            color: Colors.primary, 
            fontSize: '16px',
            padding: '6px',
            borderRadius: '4px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = Colors.totalHighlight;
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
          title="View Invoice"
        />
      </div>
    </div>
  );

  return (
    <>
      {documentLoading && <CustomLoader fullscreen />}
      <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0 }}>Transactions</Title>
        <div style={{ display: 'flex', gap: '12px' }}>
          <CustomButton
            onClick={handleCreate}
            width="auto"
            height={32}
            fontSize={12}
          >
            <PlusOutlined /> Create
          </CustomButton>
        </div>
      </div>
      
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomSelect
            placeholder="Select account type"
            value={filters.accountType}
            onChange={(value) => setFilters(prev => ({ ...prev, accountType: value }))}
            options={[
              { value: '', label: 'All Account Types' },
              { value: Constants.TRANSACTIONS.ACCOUNT_TYPE.CORPORATE, label: removeUnderScoreAndCapitalize(Constants.TRANSACTIONS.ACCOUNT_TYPE.CORPORATE) },
              { value: Constants.TRANSACTIONS.ACCOUNT_TYPE.ESCROW, label: removeUnderScoreAndCapitalize(Constants.TRANSACTIONS.ACCOUNT_TYPE.ESCROW) }
            ]}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomSelect
            placeholder="Select business status"
            value={filters.businessStatus}
            onChange={(value) => setFilters(prev => ({ ...prev, businessStatus: value }))}
            options={[
              { value: '', label: 'All Business Status' },
              { value: Constants.TRANSACTIONS.BUSINESS_STATUS.INHOUSE, label: removeUnderScoreAndCapitalize(Constants.TRANSACTIONS.BUSINESS_STATUS.INHOUSE) },
              { value: Constants.TRANSACTIONS.BUSINESS_STATUS.CLIENT, label: removeUnderScoreAndCapitalize(Constants.TRANSACTIONS.BUSINESS_STATUS.CLIENT) }
            ]}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomSelect
            placeholder="Select approval status"
            value={filters.approvalStatus}
            onChange={(value) => setFilters(prev => ({ ...prev, approvalStatus: value }))}
            options={[
              { value: '', label: 'All Approval Status' },
              { value: Constants.APPROVAL_STATUS.PENDING, label: removeUnderScoreAndCapitalize(Constants.APPROVAL_STATUS.PENDING) },
              { value: Constants.APPROVAL_STATUS.APPROVED, label: removeUnderScoreAndCapitalize(Constants.APPROVAL_STATUS.APPROVED) },
              { value: Constants.APPROVAL_STATUS.REJECTED, label: removeUnderScoreAndCapitalize(Constants.APPROVAL_STATUS.REJECTED) }
            ]}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomDatePicker
            placeholder="Start date"
            value={filters.startDate}
            onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
          />
        </Col>
        <Col xs={12} sm={12} md={4} style={{ marginBottom: '8px' }}>
          <CustomDatePicker
            placeholder="End date"
            value={filters.endDate}
            onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
          />
        </Col>
        <Col xs={24} sm={12} md={3} style={{ marginBottom: '8px' }}>
          <CustomButton
            onClick={() => setFilters(initialFilters)}
            variant="outline"
            color={Colors.error}
            width="100%"
            height={28}
            fontSize={12}
          >
            Reset Filters
          </CustomButton>
        </Col>
      </Row>
      
      <CustomTable
        columns={[
          {
            title: '#',
            key: 'index',
            render: (_, __, index) => (currentPage - 1) * limit + index + 1,
            width: 50
          },
          {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) => getDateFromDateString(text),
            width: 80
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text, record) => renderDescriptionWithToggle(text, record._id),
            width: 120
          },
          {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            render: (amount) => (
              <span style={{ 
                fontWeight: '600', 
                color: Colors.primary,
                backgroundColor: Colors.totalHighlight,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '13px'
              }}>
                ₹{amount.toFixed(2)}
              </span>
            ),
            width: 80
          },
          {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            render: (amount) => (
              <span style={{ 
                fontWeight: '600', 
                color: amount > 0 ? Colors.balanceWarning : Colors.primary,
                backgroundColor: amount > 0 ? Colors.balanceWarningLight : Colors.totalHighlight,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '13px'
              }}>
                ₹{amount.toFixed(2)}
              </span>
            ),
            width: 80
          },
          {
            title: 'Account Type',
            dataIndex: 'accountType',
            key: 'accountType',
            render: (type) => (
              <Tag color={getAccountTypeColor(type)} style={{ textTransform: 'capitalize' }}>
                {type}
              </Tag>
            ),
            width: 80
          },
          {
            title: 'Business Status',
            dataIndex: 'businessStatus',
            key: 'businessStatus',
            render: (status) => (
              <Tag color={getStatusColor(status)} style={{ textTransform: 'capitalize' }}>
                {status}
              </Tag>
            ),
            width: 80
          },
          {
            title: 'Approval Status',
            dataIndex: 'approvalStatus',
            key: 'approvalStatus',
            render: (status) => status ? (
              <Tag color={getApprovalStatusColor(status)} style={{ textTransform: 'capitalize' }}>
                {status}
              </Tag>
            ) : (
              <Tag color={Colors.gray} style={{ textTransform: 'capitalize' }}>
                N/A
              </Tag>
            ),
            width: 120
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <div style={{ display: 'flex', gap: '8px' }}>
                <Tooltip title="View Invoice" placement="top">
                  <EyeOutlined 
                    onClick={() => handleViewDocuments(record)}
                    style={{ 
                      cursor: 'pointer', 
                      color: Colors.primary,
                      padding: '4px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = Colors.totalHighlight;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  />
                </Tooltip>
                <Tooltip title="Add Installment" placement="top">
                  <PlusOutlined 
                    onClick={() => handleCreateInstallment(record._id)}
                    style={{ 
                      cursor: 'pointer', 
                      color: Colors.primary,
                      padding: '4px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = Colors.totalHighlight;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  />
                </Tooltip>
              </div>
            ),
            width: 80
          }
        ]}
        dataSource={transactions}
        loading={loading}
        rowKey={(record) => record._id}
        currentPage={currentPage}
        total={pagination?.totalTransactions || 0}
        pageSize={limit}
        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} transactions`}
        onChange={(paginationInfo) => {
          setCurrentPage(paginationInfo.current);
          setLimit(paginationInfo.pageSize);
        }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <div style={{ margin: 0, padding: '16px', backgroundColor: '#fafafa' }}>
                {record.installments && record.installments.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <h4 style={{ marginBottom: '12px', color: Colors.primary }}>Installments</h4>
                    {record.installments.map((installment, index) => renderInstallmentCard(installment, index))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#999' }}>
                    No installments found for this transaction
                  </div>
                )}
              </div>
            );
          },
          showExpandColumn: false,
          expandRowByClick: true,
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [record._id] : []);
          },
        }}
        scroll={{ x: 1000 }}
      />
      
      <CreateTransactionModal
        isOpen={createModalOpen}
        onClose={handleCloseCreateModal}
        parentTransactionId={parentTransactionId}
      />
      </Card>
    </>
  );
};

export default Transactions;